/**
 * User Management Feature - Users Listing Hook
 * Handles fetching users and locations with proper error tracking and logging
 */

import { useEffect, useMemo } from 'react';
import { useLogger } from '@/lib/logger';
import { useOrganization } from '@/contexts/OrganizationContext';
import * as Sentry from '@sentry/react';
import { trpc } from '@/lib/trpc';
import { excludeDelegatedLocations } from '@/features/locations/utils/delegatedLocations';
import type { Location } from '../types';

export function useUsers() {
  const logger = useLogger();
  const { organization } = useOrganization();

  const query = trpc.users.list.useQuery(undefined, {
    enabled: !!organization?.id,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  // Handle logging with useEffect (React Query v5 removed onSuccess/onError)
  useEffect(() => {
    if (query.data) {
      logger.info('Users fetched successfully', {
        operation: 'fetch_users',
        organizationId: organization?.id,
        count: query.data.length,
      });
    }

    if (query.error) {
      Sentry.captureException(query.error, {
        tags: {
          feature: 'user-management',
          operation: 'fetch_users',
        },
      });

      logger.error('Failed to fetch users', {
        operation: 'fetch_users',
        error: query.error.message,
      });
    }
  }, [query.data, query.error, logger, organization?.id]);

  return {
    users: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useUserManagementLocations() {
  const logger = useLogger();
  const { organization } = useOrganization();

  const locationsQuery = trpc.locations.list.useQuery(
    { includeInactive: false, organizationId: organization?.id },
    { enabled: !!organization?.id, staleTime: 30_000 }
  );

  useEffect(() => {
    if (locationsQuery.data) {
      logger.info('Locations fetched successfully', {
        operation: 'fetch_locations',
        count: locationsQuery.data.length,
        organizationId: organization?.id,
      });
    }

    if (locationsQuery.error) {
      Sentry.captureException(locationsQuery.error, {
        tags: {
          feature: 'user-management',
          operation: 'fetch_locations',
        },
      });

      logger.error('Failed to fetch locations', {
        operation: 'fetch_locations',
        error: locationsQuery.error.message,
      });
    }
  }, [locationsQuery.data, locationsQuery.error, logger, organization?.id]);

  /**
   * BLU-879: delegated ("shadow") locations are dropped. Assigning a user to a
   * location whose fulfilment is routed elsewhere is meaningless — nobody works
   * a floor someone else runs.
   *
   * Filtered on the RAW rows (which carry `delegated_to_location_id`) before the
   * cast, via the shared `excludeDelegatedLocations`, and MEMOISED: the
   * previous expression returned `locationsQuery.data` itself and was
   * reference-stable, so a bare `.filter()` here would hand every consumer a
   * new array identity on every render.
   */
  const locations = useMemo(
    () => excludeDelegatedLocations(locationsQuery.data ?? []) as Location[],
    [locationsQuery.data]
  );

  return {
    locations,
    isLoading: locationsQuery.isLoading,
  };
}
